import React, { useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { useToast } from "../../hooks/useToast";
import { authService } from "../../services/authService";

interface GuestAccessFormProps {
  onSignIn: () => void;
  onSignUp: () => void;
  onSuccess: () => void;
}

export const GuestAccessForm: React.FC<GuestAccessFormProps> = ({
  onSignIn,
  onSignUp,
  onSuccess,
}) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const guestAccess = useAuthStore((state) => state.guestAccess);
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await guestAccess(password);
      showToast("Successfully signed in as guest", "success");
      onSuccess();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Failed to access as guest");
      }
      showToast("Failed to access as guest", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Enter the project password to access comments as a guest. No account
        required.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="projectPassword"
            className="block text-sm font-medium text-gray-700"
          >
            Project Password
          </label>
          <input
            type="text"
            id="projectPassword"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            placeholder="Enter project password"
          />
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading || !password.trim()}
          className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
        >
          {isLoading ? "Accessing..." : "Continue as Guest"}
        </button>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-2 text-gray-500">
              Or continue with
            </span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={onSignIn}
            className="flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Sign In
          </button>
          <button
            onClick={onSignUp}
            className="flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Sign Up
          </button>
        </div>
      </div>
    </div>
  );
};
